import React, {ReactElement, ReactNode, useEffect, useState} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {motion} from 'motion/react'

import {cssTheme} from '../../../themes'
import {linariaMq} from '../../../utils/breakpoints'
import minus from '../../../utils/minus'
import {Button} from '../Button'
import {Box} from '../helpers/Box'
import {OutsideClickHandler} from '../helpers/OutsideClickHandler'
import {Icon} from '../Icon'
import {Text} from '../Text'

export interface Props {
  /** Pass through classname to allow styles overrides */
  className?: string
  /** An icon placed before the Toast content. It can be any type of React Element */
  icon?: ReactElement
  /** The content of the Toast, a React component or pure text */
  children: ReactNode
  /** The header content of the Toast, a React component or pure text */
  title?: string
  /** Toast closing will trigger the callback function. It will be called both on press close button and on self-closing */
  onClose?: () => void
  /** Toast click will trigger the callback function. */
  onClick?: (event: React.SyntheticEvent) => void
  /** Callback function that will fire when the user clicks on the close button. If an onClose callback is passed in, it will be called along with this callback. */
  onCloseButtonClick?: () => void
  /** Determines whether the Toast will be auto closed or not.*/
  autoClose?: boolean
  /** Time in milliseconds before the Toast auto closes. Defaults to HIDE_TIMEOUT. */
  hideTimeout?: number
  /** Determines whether the Toast will be closed on outside click or not.*/
  closeOnOutsideClick?: boolean
  /** Whether to show the close button. */
  showCloseButton?: boolean
  /** Style prop is used by Linaria to pass CSS variables in cases when we need to style custom components in this way: `const StyledToast = styled(Toast)` */
  style?: React.CSSProperties
}

const ToastContainer = styled(motion.div)`
  border-radius: ${cssTheme.layout.radius.md};
  box-shadow: ${cssTheme.shadows.floating};
  margin: 0 ${cssTheme.layout.spacing.s500};
  width: calc(100vw - 2 * ${cssTheme.layout.spacing.s500});
  overflow: hidden;
  animation: slide-in 0.2s;
  pointer-events: auto;

  ${linariaMq.desktopXs} {
    min-width: 366px;
    max-width: 712px;
  }
`

const IconWrapper = styled.div`
  display: flex;
  color: ${cssTheme.colors.toast.content};
  height: ${cssTheme.layout.spacing.s600};
  align-items: center;
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: ${cssTheme.layout.spacing.s200};
`

const ButtonWrapper = styled.div`
  display: flex;
`

const CloseButton = styled(Button)`
  margin-inline-end: ${minus(cssTheme.layout.spacing.s250)};
`

const Row = styled(Box)`
  padding-inline: ${cssTheme.layout.spacing.s600};
  &.--has-icon {
    padding-inline-start: ${cssTheme.layout.spacing.s400};
  }
  &.--has-close-button {
    padding-inline-end: ${cssTheme.layout.spacing.s400};
  }
`

const Content = styled.div`
  ${cssTheme.typography.bodyS}
  color: ${cssTheme.colors.toast.content};
`

const HIDE_TIMEOUT = 3000

export const Toast = ({
  icon,
  onClose,
  onCloseButtonClick,
  onClick,
  children,
  title,
  showCloseButton = false,
  autoClose = true,
  hideTimeout = HIDE_TIMEOUT,
  closeOnOutsideClick = false,
  className,
  style
}: Props) => {
  const [timeoutId, setTimeoutId] = useState<number>()

  useEffect(() => {
    if (autoClose) {
      setTimeoutId(window.setTimeout(toggleToast(false), hideTimeout))
    }

    return () => window.clearTimeout(timeoutId)
  }, [])

  const toggleToast = (to: boolean) => () => {
    if (!to && onClose) {
      window.setTimeout(onClose, 200)
    }
  }

  const handleMouseEnter = (): void => {
    window.clearTimeout(timeoutId)
  }

  const handleMouseLeave = (): void => {
    if (autoClose) {
      setTimeoutId(window.setTimeout(toggleToast(false), hideTimeout))
    }
  }

  const handleCloseClick = () => {
    if (onCloseButtonClick) onCloseButtonClick()
    if (onClose) onClose()
  }

  const handleOutsideClick = () => {
    if (closeOnOutsideClick && onClose) onClose()
  }

  return (
    <OutsideClickHandler onOutsideClick={handleOutsideClick}>
      <ToastContainer
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        initial={{opacity: 0, y: 20}}
        animate={{opacity: 1, y: 0}}
        exit={{opacity: 0}}
        className={className}
        style={style}
      >
        <Row
          display="flex"
          backgroundColor="toast.background"
          position="relative"
          overflow="hidden"
          zIndex="0"
          paddingTop="s500"
          paddingBottom="s500"
          alignItems="flex-start"
          gap="s400"
          className={cx(
            icon && '--has-icon',
            showCloseButton && '--has-close-button'
          )}
        >
          {icon && <IconWrapper>{icon}</IconWrapper>}
          <ContentWrapper>
            {title && (
              <Text variant="labelM" colorPath="toast.content">
                {title}
              </Text>
            )}
            <Content>{children}</Content>
          </ContentWrapper>
          {showCloseButton && (
            <ButtonWrapper>
              <CloseButton
                hasTouchArea
                variant="ghostInverse"
                size="sm"
                iconStart={
                  <Icon name="Close" size="sm" colorPath="toast.content" />
                }
                onClick={handleCloseClick}
              />
            </ButtonWrapper>
          )}
        </Row>
      </ToastContainer>
    </OutsideClickHandler>
  )
}
